/**
 * useEPIFormWizard Hook
 * State management for EPI Form Wizard
 *
 * @version 2025.1.0
 */

import { useState, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';
import { TipoDeRisco, EpiStatus, EpiFormData } from '../../../../../types/epi';
import {
  EPIWizardState,
  EPIWizardStep,
  EPI_WIZARD_STEPS,
  UseEPIFormWizardReturn,
} from '../EPIFormWizard.types';

// Validation schemas per step
const identificationSchema = yup.object({
  nome: yup
    .string()
    .required('Nome do EPI é obrigatório')
    .min(3, 'Mínimo de 3 caracteres')
    .max(255, 'Máximo de 255 caracteres'),
  tipoDeRisco: yup
    .string()
    .oneOf(Object.values(TipoDeRisco), 'Tipo de risco inválido')
    .required('Tipo de risco é obrigatório'),
  risco: yup
    .string()
    .required('Descrição do risco é obrigatória')
    .max(255, 'Máximo de 255 caracteres'),
  ativo: yup.string().oneOf(Object.values(EpiStatus)).required(),
});

const certificateSchema = yup.object({
  metodo: yup
    .string()
    .required('Número do C.A. é obrigatório')
    .max(255, 'Máximo de 255 caracteres'),
  inicioValidade: yup.string().nullable(),
  fimValidade: yup
    .string()
    .required('Data de validade do CA é obrigatória')
    .test('not-past', 'Não é permitido cadastrar EPI com CA vencido', function(value) {
      if (!value) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expirationDate = new Date(value);
      return expirationDate >= today;
    }),
  atenuacao: yup.string().max(100, 'Máximo de 100 caracteres'),
  eSocial: yup.string().max(50, 'Máximo de 50 caracteres'),
});

const stockSchema = yup.object({
  quantidade: yup
    .number()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .min(0, 'Quantidade não pode ser negativa')
    .integer('Quantidade deve ser um número inteiro'),
  unidade: yup.string().max(50, 'Máximo de 50 caracteres'),
  custo: yup
    .number()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .min(0, 'Custo não pode ser negativo'),
  observacao: yup.string().max(2000, 'Máximo de 2000 caracteres'),
});

// Map step keys to their validation schemas
const stepSchemas: Record<EPIWizardStep, yup.AnyObjectSchema> = {
  identification: identificationSchema,
  certificate: certificateSchema,
  stock: stockSchema,
};

// Fields to validate per step
const stepFields: Record<EPIWizardStep, (keyof EpiFormData)[]> = {
  identification: ['nome', 'tipoDeRisco', 'risco', 'ativo'],
  certificate: ['metodo', 'inicioValidade', 'fimValidade', 'atenuacao', 'eSocial'],
  stock: ['quantidade', 'unidade', 'custo', 'observacao'],
};

// Initial state
const createInitialState = (): EPIWizardState => ({
  activeStep: 0,
  formData: {},
  loading: false,
  error: null,
});

/**
 * Hook for managing EPI Form Wizard state and actions
 */
export function useEPIFormWizard(): UseEPIFormWizardReturn {
  const [state, setState] = useState<EPIWizardState>(createInitialState);
  const { trigger, getValues, setError: setFormError } = useFormContext<EpiFormData>();

  // Current step info
  const currentStep = EPI_WIZARD_STEPS[state.activeStep];
  const currentStepKey = currentStep?.key || 'identification';
  const isFirstStep = state.activeStep === 0;
  const isLastStep = state.activeStep === EPI_WIZARD_STEPS.length - 1;
  const canSkip = currentStep?.optional || false;

  /**
   * Validate current step fields
   */
  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    const fields = stepFields[currentStepKey];
    const schema = stepSchemas[currentStepKey];
    const values = getValues();

    try {
      // Get only current step values
      const stepValues: Partial<EpiFormData> = {};
      fields.forEach((field) => {
        stepValues[field] = values[field] as any;
      });

      // Validate with yup schema
      await schema.validate(stepValues, { abortEarly: false });

      // Also trigger react-hook-form validation for the fields
      const result = await trigger(fields);
      return result;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        // Set form errors for each field
        err.inner.forEach((error) => {
          if (error.path) {
            setFormError(error.path as keyof EpiFormData, {
              type: 'manual',
              message: error.message,
            });
          }
        });
      }
      return false;
    }
  }, [currentStepKey, getValues, trigger, setFormError]);

  /**
   * Handle moving to next step
   */
  const handleNext = useCallback(async (): Promise<boolean> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const isValid = await validateCurrentStep();

      if (!isValid) {
        setState((prev) => ({ ...prev, loading: false }));
        return false;
      }

      if (!isLastStep) {
        setState((prev) => ({
          ...prev,
          activeStep: prev.activeStep + 1,
          loading: false,
        }));
      } else {
        setState((prev) => ({ ...prev, loading: false }));
      }

      return true;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: 'Erro ao validar campos. Verifique os dados.',
      }));
      return false;
    }
  }, [validateCurrentStep, isLastStep]);

  /**
   * Handle going back to previous step
   */
  const handleBack = useCallback(() => {
    if (isFirstStep) return;

    setState((prev) => ({
      ...prev,
      activeStep: prev.activeStep - 1,
      error: null,
    }));
  }, [isFirstStep]);

  /**
   * Skip current step (only for optional steps)
   */
  const handleSkip = useCallback(() => {
    if (!canSkip || isLastStep) return;

    setState((prev) => ({
      ...prev,
      activeStep: prev.activeStep + 1,
      error: null,
    }));
  }, [canSkip, isLastStep]);

  /**
   * Set error message
   */
  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  /**
   * Reset wizard state
   */
  const reset = useCallback(() => {
    setState(createInitialState());
  }, []);

  return {
    state,
    steps: EPI_WIZARD_STEPS,
    isFirstStep,
    isLastStep,
    currentStepKey,
    canSkip,
    handleNext,
    handleBack,
    handleSkip,
    setError,
    reset,
  };
}

export default useEPIFormWizard;
